import { ORDER_SERVICES_TYPES } from 'features/order-tracker/utils/entries';

import {
  ADD_MILESTONE,
  CLEAR_STATE,
  DELETE_MILESTONE,
  FETCH_DELIVERY_DETAILS_SUCCEED,
  FETCH_GROUP_ORDER_SUCCEED,
  FETCH_ORDER_SUMMARY_SUCCEED,
  FETCH_TRACKING_EVENTS_SUCCEED,
  INIT_SUMMARY_PAGE_START,
  SET_API_FAILED,
  SET_API_FINISHED,
  SET_DATA_TO_STORE,
  UPDATE_APPOINTMENT_BY_ID,
} from '../actions';
import { useLivePersonPageChange } from '../hooks/useLivePersonPageChange';
import { sortMilestones } from './helpers';

type OrderMilestone = common.OrderMilestone;

interface AppointmentsInfo {
  engineerAppointments?: Array<{
    orderItemReference: string;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

interface OrderSummaryState {
  isFetching: boolean;
  isGOCFetched: boolean;
  isGeneralAPIFailed: boolean;
  errorMessage: string;
  orderReference: string;
  postcode: string;
  isDeliveryDetailsFailed: boolean;
  isTrackingEventsFailed: boolean;
  products: unknown[];
  ordersNumbers: string[];
  appointmentsInfo: AppointmentsInfo;
  deliveriesInfo: Record<string, unknown>;
  orderMilestones: OrderMilestone[];
  [key: string]: unknown;
}

interface Action {
  type: string;
  payload?: unknown;
}

export const initialState: OrderSummaryState = {
  isFetching: true,
  isGOCFetched: false,
  isGeneralAPIFailed: false,
  errorMessage: '',
  orderReference: '',
  postcode: '',
  isDeliveryDetailsFailed: false,
  isTrackingEventsFailed: false,
  products: [],
  ordersNumbers: [],
  appointmentsInfo: {},
  deliveriesInfo: {},
  orderMilestones: [],
};

export const orderSummary = (
  state: OrderSummaryState = initialState, { type, payload }: Action,
): OrderSummaryState => {
  switch (type) {
    case INIT_SUMMARY_PAGE_START:
      return {
        ...state,
        isFetching: true,
      };

    case SET_DATA_TO_STORE:
      return {
        ...state,
        ...(payload as Partial<OrderSummaryState>),
        ordersNumbers: [(payload as Record<string, string>).orderReference],
      };

    case SET_API_FINISHED:
      useLivePersonPageChange();
      return {
        ...state,
        isFetching: false,
      };

    case FETCH_GROUP_ORDER_SUCCEED:
    case FETCH_ORDER_SUMMARY_SUCCEED:
    case FETCH_DELIVERY_DETAILS_SUCCEED:
    case SET_API_FAILED:
      return {
        ...state,
        ...(payload as Partial<OrderSummaryState>),
        orderMilestones: sortMilestones([
          ...state.orderMilestones,
          ...(((payload as Record<string, unknown>).orderMilestones as unknown as OrderMilestone[]) || []),
        ]),
      };

    case UPDATE_APPOINTMENT_BY_ID: {
      const updatePayload = payload as { itemId: string; updatedApptData: Record<string, unknown> };
      return {
        ...state,
        appointmentsInfo: {
          ...state.appointmentsInfo,
          engineerAppointments: state.appointmentsInfo.engineerAppointments?.map((item) => {
            if (item.orderItemReference === updatePayload.itemId) {
              return {
                ...item,
                ...updatePayload.updatedApptData,
              };
            }

            return item;
          }),
        },
      };
    }

    case FETCH_TRACKING_EVENTS_SUCCEED:
      return {
        ...state,
        ...(payload as Partial<OrderSummaryState>),
        orderMilestones: sortMilestones([
          ...state.orderMilestones.filter((milestone) => milestone.type !== ORDER_SERVICES_TYPES.Delivery),
          ...((payload as Record<string, unknown>).orderMilestones as unknown as OrderMilestone[] || []),
        ]),
      };

    case CLEAR_STATE:
      return initialState;

    case DELETE_MILESTONE:
      return {
        ...state,
        orderMilestones: state.orderMilestones.filter(({ id }) => id !== payload),
      };

    case ADD_MILESTONE:
      return {
        ...state,
        orderMilestones: sortMilestones([...state.orderMilestones, ...(payload as OrderMilestone[])]),
      };

    default:
      return state;
  }
};
